"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Typography } from "@/components/typography/typography";
import { useMediaQuery } from "@/hook/use-media-query";
import { productService } from "@/services/product";
import useUserStore from "@/global-store/user";
import { useDebounce } from "@/hook/use-debounce";
import { useUserStoreHydrated } from "@/hook/use-user-store-hydrated";
import LoadingIcon from "@/assets/icons/loading-icon";
import { priceSymbol } from "@/utils/price-count";
import { categoryService } from "@/services/category";
import { CustomRadio } from "./CustomRadio";
import { Modal } from "../modal";
import BreadCrumb from "../breadcrumb/breadcrumb";
import DismissibleChip from "../dismissible-chip/dismissible-chip";
import { InfiniteLoader } from "../infinite-loader";
import CheckboxList from "./CustomCheckbox";
import { ProductCardUI } from "../product-card/product-card-ui";

interface SortOption {
  id: number;
  title: string;
  sortBy?: string;
  sortOrder?: string;
}

interface QueryParams {
  minPrice?: number | undefined;
  maxPrice?: number | undefined;
  sortBy?: string;
  sortOrder?: string;
  minDiscount?: number | undefined;
  maxDiscount?: number | undefined;
  discount?: boolean;
  name?: string;
}

const priceRange = [
  { id: 1, title: "All Price" },
  { id: 2, title: `Under ${priceSymbol(20)}`, minPrice: 1, maxPrice: 20 },
  {
    id: 3,
    title: `${priceSymbol(20)} to ${priceSymbol(100)}`,
    minPrice: 20,
    maxPrice: 100,
  },
  {
    id: 4,
    title: `${priceSymbol(100)} to ${priceSymbol(300)}`,
    minPrice: 100,
    maxPrice: 300,
  },
  {
    id: 5,
    title: `${priceSymbol(300)} to ${priceSymbol(500)}`,
    minPrice: 300,
    maxPrice: 500,
  },
  {
    id: 6,
    title: `${priceSymbol(500)} to ${priceSymbol("1,000")}`,
    minPrice: 500,
    maxPrice: 1000,
  },
  {
    id: 7,
    title: `${priceSymbol("1,000")} to ${priceSymbol("10,000")}`,
    minPrice: 1000,
    maxPrice: 10000,
  },
];

const discountRange = [
  { id: 1, title: "All Discounts", discount: true },
  {
    id: 2,
    title: `Under ${priceSymbol(20)}`,
    discount: undefined,
    minDiscount: 1,
    maxDiscount: 20,
  },
  {
    id: 3,
    title: `${priceSymbol(20)} to ${priceSymbol(100)}`,
    discount: undefined,
    minDiscount: 20,
    maxDiscount: 100,
  },
  {
    id: 4,
    title: `${priceSymbol(100)} to ${priceSymbol(300)}`,
    discount: undefined,
    minDiscount: 100,
    maxDiscount: 300,
  },
  {
    id: 5,
    title: `${priceSymbol(300)} to ${priceSymbol(500)}`,
    discount: undefined,
    minDiscount: 300,
    maxDiscount: 500,
  },
];

const popularitySortOption: SortOption = {
  id: 0,
  title: "Popularity",
  sortBy: "priorityOrder",
  sortOrder: "asc",
};

const baseSortOptions: SortOption[] = [
  { id: 1, title: "Newest Arrivals" },
  { id: 2, title: "Price: Low to High", sortBy: "price", sortOrder: "asc" },
  { id: 3, title: "Price: High to Low", sortBy: "price", sortOrder: "desc" },
  { id: 4, title: "Name: A to Z", sortBy: "name", sortOrder: "asc" },
  { id: 5, title: "Name: Z to A", sortBy: "name", sortOrder: "desc" },
];

const getSortOptionsForUser = (isCompanyUser: boolean) =>
  isCompanyUser ? [popularitySortOption, ...baseSortOptions] : baseSortOptions;

const getDefaultSortOption = (isCompanyUser: boolean) =>
  isCompanyUser ? popularitySortOption : baseSortOptions[0];

const COMPANY_ONLY_SORT_BY = "priorityOrder";

const getSortParamsForApi = (
  option: SortOption,
  isCompanyUser: boolean,
): { sortBy?: string; sortOrder?: string } => {
  if (!option.sortBy) {
    return {};
  }
  if (!isCompanyUser && option.sortBy === COMPANY_ONLY_SORT_BY) {
    return {};
  }
  return { sortBy: option.sortBy, sortOrder: option.sortOrder };
};

interface ProductsProps {
  initialCategorySlugs?: string[];
  categoryName?: string;
}

export const Products = ({
  initialCategorySlugs,
  categoryName,
}: ProductsProps) => {
  const hasHydrated = useUserStoreHydrated();
  const user = useUserStore((s) => s.user);
  const me = useUserStore((s) => s.me);
  const isCompanyUser = hasHydrated && !!user;
  const searchParams =
    typeof window !== "undefined" ? window.location.search : undefined;

  const [selectedPrice, setSelectedPrice] = useState<string>(
    priceRange[0].title,
  );
  const [selectedDiscount, setSelectedDiscount] = useState<string | undefined>(
    undefined,
  );
  const [minDiscount, setMinDiscount] = useState<number | undefined>(undefined);
  const [maxDiscount, setMaxDiscount] = useState<number | undefined>(undefined);
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string[]>(
    initialCategorySlugs ?? [],
  );
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    baseSortOptions[0],
  );
  const sortOptionsList = useMemo(
    () => getSortOptionsForUser(isCompanyUser),
    [isCompanyUser],
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const isXl = useMediaQuery("(min-width: 1280px)");

  useEffect(() => {
    window.scroll(0, 0);
  }, [
    selectedCategory,
    selectedPrice,
    selectedDiscount,
    selectedSortOption,
    searchQuery,
  ]);

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    data: categories,
    fetchNextPage: fetchNextCategories,
    hasNextPage: hasNextCategories,
    isLoading: isCategoriesLoading,
    isFetchingNextPage: isFetchingNextCategories,
  } = useInfiniteQuery(
    ["categories", isCompanyUser],
    ({ pageParam = 1 }) =>
      categoryService.getCategoriesForUser(isCompanyUser, {
        page: pageParam,
        limit: 8,
      }),
    {
      enabled: hasHydrated,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.total > allPages.length * 8) {
          return allPages.length + 1;
        }
        return undefined;
      },
    },
  );

  const allCategories = useMemo(
    () =>
      categories?.pages
        .map((category) =>
          category.data.data.map(({ name, slug }) => ({ name, slug })),
        )
        .filter(Boolean)
        .flat(1) || [],
    [categories],
  );

  useEffect(() => {
    const filtersApplied =
      selectedCategory.length > 0 ||
      selectedPrice !== priceRange[0].title ||
      selectedDiscount !== undefined;
    setIsFilterApplied(filtersApplied);
  }, [selectedCategory, selectedPrice, selectedDiscount]);

  useEffect(() => {
    const parseCategorySlugs = (params: URLSearchParams) => {
      // Current format: ?category=slug-a,slug-b
      const categoryParam = params.get("category");
      if (categoryParam) {
        setSelectedCategory(categoryParam.split(",").filter(Boolean));
        return;
      }

      // Legacy format: ?categorySlugs=["slug-a"] (JSON) or ?categorySlugs=a,b
      const categorySlugsParam = params.get("categorySlugs");
      if (categorySlugsParam) {
        try {
          const parsedCategorySlugs = categorySlugsParam.startsWith("[")
            ? JSON.parse(categorySlugsParam)
            : categorySlugsParam.split(",");
          setSelectedCategory(parsedCategorySlugs);
        } catch {
          setSelectedCategory([categorySlugsParam]);
        }
      }
    };

    const parsePriceParams = (params: URLSearchParams) => {
      const minPriceParam = params.get("minPrice");
      const maxPriceParam = params.get("maxPrice");
      if (minPriceParam !== null) {
        setMinPrice(Number(minPriceParam));
      }
      if (maxPriceParam !== null) {
        setMaxPrice(Number(maxPriceParam));
      }
    };

    const updateSelectedPrice = (params: URLSearchParams) => {
      const minPriceParam = params.get("minPrice");
      const maxPriceParam = params.get("maxPrice");
      if (minPriceParam !== null || maxPriceParam !== null) {
        const selectedRange = priceRange.find(
          (price) =>
            price.minPrice === Number(minPriceParam) &&
            price.maxPrice === Number(maxPriceParam),
        );
        if (selectedRange) {
          setSelectedPrice(selectedRange.title);
        }
      } else {
        setSelectedPrice(priceRange[0].title);
      }
    };

    const updateSelectedSortOption = (params: URLSearchParams) => {
      const options = getSortOptionsForUser(isCompanyUser);
      const sortByParam = params.get("sortBy");
      const sortOrderParam = params.get("sortOrder");
      if (sortByParam !== null && sortOrderParam !== null) {
        const selectedOption = options.find(
          (option) =>
            option.sortBy === sortByParam &&
            option.sortOrder === sortOrderParam,
        );
        if (selectedOption) {
          setSelectedSortOption(selectedOption);
          return;
        }
      }
      setSelectedSortOption(getDefaultSortOption(isCompanyUser));
    };

    const parseDiscountParams = (params: URLSearchParams) => {
      const minDiscountParam = params.get("minDiscount");
      const maxDiscountParam = params.get("maxDiscount");
      const discountParam = params.get("discount");

      if (minDiscountParam !== null || maxDiscountParam !== null) {
        const selectedRange = discountRange.find(
          (discount) =>
            discount.minDiscount === Number(minDiscountParam) &&
            discount.maxDiscount === Number(maxDiscountParam),
        );
        if (selectedRange) {
          setSelectedDiscount(selectedRange.title);
          setMinDiscount(selectedRange.minDiscount);
          setMaxDiscount(selectedRange.maxDiscount);
        }
      } else if (discountParam !== null) {
        setSelectedDiscount(discountRange[0].title);
      } else {
        setSelectedDiscount(undefined);
        setMinDiscount(undefined);
        setMaxDiscount(undefined);
      }
    };

    const handleURLChange = () => {
      if (!hasHydrated) return;
      const params = new URLSearchParams(window.location.search);
      parseCategorySlugs(params);
      parsePriceParams(params);
      updateSelectedPrice(params);
      updateSelectedSortOption(params);
      parseDiscountParams(params);
    };

    handleURLChange();
  }, [searchParams, allCategories, isCompanyUser, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated) return;
    const options = getSortOptionsForUser(isCompanyUser);
    if (!options.some((option) => option.id === selectedSortOption.id)) {
      setSelectedSortOption(getDefaultSortOption(isCompanyUser));
    }
  }, [isCompanyUser, selectedSortOption.id, hasHydrated]);

  const updateQueryParams = (newParams: Partial<QueryParams>) => {
    const currentParams = new URLSearchParams(window.location.search);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined) {
        currentParams.set(key, String(value));
      } else {
        currentParams.delete(key);
      }
    });

    const newQueryString = currentParams.toString();
    const newUrl = `${window.location.pathname}?${newQueryString}`;
    window.history.pushState({}, "", newUrl);
  };

  // Writes /products?category=slug-a,slug-b (or /products when none).
  // Built manually so the commas stay readable instead of encoding to %2C
  const updateCategoryUrl = (slugs: string[]) => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete("category");
    currentParams.delete("categorySlugs");

    const categoryQuery = slugs.length ? `category=${slugs.join(",")}` : "";
    const queryString = [categoryQuery, currentParams.toString()]
      .filter(Boolean)
      .join("&");
    window.history.pushState(
      {},
      "",
      queryString ? `/products?${queryString}` : "/products",
    );
  };

  const handleCategoryChange = (newSelectedCategories: string[]) => {
    // Keep selected slugs whose category isn't in the paginated sidebar list
    // yet (e.g. entered via a /category/<slug> deep link)
    const loadedSlugs = allCategories.map((category) => category.slug);
    const unloadedSelectedSlugs = selectedCategory.filter(
      (slug) => !loadedSlugs.includes(slug),
    );
    const selectedSlugs = [
      ...unloadedSelectedSlugs,
      ...allCategories
        .filter((category) => newSelectedCategories.includes(category.name))
        .map((category) => category.slug),
    ];

    setSelectedCategory(selectedSlugs);
    updateCategoryUrl(selectedSlugs);
  };

  const handleRemoveFilter = (categorySlug: string) => {
    const updatedCategories = selectedCategory.filter(
      (c) => c !== categorySlug,
    );
    setSelectedCategory(updatedCategories);
    updateCategoryUrl(updatedCategories);
  };

  const handleRemovePriceFilter = () => {
    setSelectedPrice(priceRange[0].title);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    updateQueryParams({ minPrice: undefined, maxPrice: undefined });
  };

  const handleRemoveDiscountFilter = () => {
    setSelectedDiscount(undefined);
    setMinDiscount(undefined);
    setMaxDiscount(undefined);
    updateQueryParams({
      discount: undefined,
      minDiscount: undefined,
      maxDiscount: undefined,
    });
  };

  const getCategoryNameFromSlug = (slug: string) => {
    const matchedCategory = allCategories.find(
      (category) => category.slug === slug,
    );
    if (matchedCategory) {
      return matchedCategory.name;
    }
    if (categoryName && initialCategorySlugs?.includes(slug)) {
      return categoryName;
    }
    return slug;
  };

  if (isXl && isFilterModalOpen) {
    setIsFilterModalOpen(false);
  }

  const handleSelectSortOption = (option: SortOption) => {
    setSelectedSortOption(option);
    updateQueryParams({
      sortBy: option.sortBy ?? undefined,
      sortOrder: option.sortOrder ?? undefined,
    });
  };

  const handlePriceChange = (price: {
    id: number;
    title: string;
    minPrice?: number;
    maxPrice?: number;
  }) => {
    setSelectedPrice(price.title);
    setMinPrice(price.minPrice);
    setMaxPrice(price.maxPrice);
    updateQueryParams({
      minPrice: price.minPrice ?? undefined,
      maxPrice: price.maxPrice ?? undefined,
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value?.trimStart());
  };

  const handleSearchSubmit = () => {
    updateQueryParams({ name: searchQuery.trimStart() || undefined });
  };

  const clearSearchQuery = () => {
    setSearchQuery("");
    updateQueryParams({ name: undefined });
  };

  const handleDiscountChange = (discount: {
    id: number;
    title: string;
    minDiscount?: number;
    maxDiscount?: number;
    discount?: boolean;
  }) => {
    setSelectedDiscount(discount.title);
    setMinDiscount(discount.minDiscount);
    setMaxDiscount(discount.maxDiscount);
    updateQueryParams({
      discount: discount.discount ?? undefined,
      minDiscount: discount.minDiscount ?? undefined,
      maxDiscount: discount.maxDiscount ?? undefined,
    });
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: products,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      "products-filter",
      isCompanyUser,
      me?.company?.id,
      selectedCategory,
      minPrice,
      maxPrice,
      debouncedSearchQuery,
      selectedSortOption,
      selectedDiscount,
      minDiscount,
      maxDiscount,
    ],
    ({ pageParam = 1 }) => {
      const sortParams = getSortParamsForApi(selectedSortOption, isCompanyUser);
      const sharedParams = {
        page: pageParam,
        limit: 8,
        categorySlugs: JSON.stringify(selectedCategory) || undefined,
        minPrice,
        maxPrice,
        name: debouncedSearchQuery?.trim() || undefined,
        ...sortParams,
        discount:
          selectedDiscount === discountRange[0].title ? true : undefined,
        minDiscount,
        maxDiscount,
      };

      return isCompanyUser
        ? productService.getCompanyProducts({
            ...sharedParams,
            companyIds: me?.company?.id != null ? [me.company.id] : undefined,
          })
        : productService.getAllProducts(sharedParams);
    },
    {
      enabled: hasHydrated,
      suspense: hasHydrated,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.total > allPages.length * 8) {
          return allPages.length + 1;
        }
        return undefined;
      },
    },
  );

  const filterPanel = (
    <div className="flex flex-col justify-start max-w-[270px] w-full p-4 sm:p-6 xl:p-5">
      {/* Category Section */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h7"
            />
          </svg>
          <Typography
            tag="h2"
            className="text-sm font-semibold text-gray-800 uppercase tracking-wider"
          >
            Category
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          {isCategoriesLoading ? (
            <div className="flex flex-col gap-3 p-1">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-[18px] w-[18px] bg-gray-100 rounded animate-pulse" />
                  <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <InfiniteLoader
              loadMore={fetchNextCategories}
              hasMore={hasNextCategories}
              loading={isFetchingNextCategories}
              manualLoad
            >
              <CheckboxList
                items={allCategories.map(({ name }) => name)}
                selectedItems={allCategories
                  .filter((category) =>
                    selectedCategory.includes(category.slug),
                  )
                  .map((category) => category.name)}
                onChange={handleCategoryChange}
              />
            </InfiniteLoader>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 my-5" />

      {/* Price Range Section */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <Typography
            tag="h2"
            className="text-sm font-semibold text-gray-800 uppercase tracking-wider"
          >
            Price Range
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          {priceRange.map((price) => (
            <CustomRadio
              key={price.id}
              name="price"
              value={price.title}
              label={price.title}
              checked={selectedPrice === price.title}
              onChange={() => handlePriceChange(price)}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 my-5" />

      {/* Discount Range Section */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
          <Typography
            tag="h2"
            className="text-sm font-semibold text-gray-800 uppercase tracking-wider"
          >
            Discount Range
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          {discountRange.map((discount) => (
            <CustomRadio
              key={discount.id}
              name="discount"
              value={discount.title}
              label={discount.title}
              checked={selectedDiscount === discount.title}
              onChange={() => handleDiscountChange(discount)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const handleClearAllFilters = () => {
    setSelectedCategory([]);
    setSelectedPrice(priceRange[0].title);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setSelectedDiscount(undefined);
    setMinDiscount(undefined);
    setMaxDiscount(undefined);
    const defaultSort = getDefaultSortOption(isCompanyUser);
    setSelectedSortOption(defaultSort);
    // Reset to /products (leaves /category/<slug> pages), keeping only the
    // user's default sort in the URL
    const defaultSortParams = new URLSearchParams();
    if (defaultSort.sortBy && defaultSort.sortOrder) {
      defaultSortParams.set("sortBy", defaultSort.sortBy);
      defaultSortParams.set("sortOrder", defaultSort.sortOrder);
    }
    const defaultSortQuery = defaultSortParams.toString();
    window.history.pushState(
      {},
      "",
      defaultSortQuery ? `/products?${defaultSortQuery}` : "/products",
    );
    setSearchQuery("");
  };

  if (!hasHydrated) {
    return (
      <section className="bg-pageBg min-h-[50vh] flex items-center justify-center">
        <LoadingIcon size={40} />
      </section>
    );
  }

  return (
    <section className="bg-pageBg">
      <div className="bg-breadcrumb px-2 md:px-4 border-b border-gray-200">
        <BreadCrumb
          routes={[
            { name: "Home", href: "/" },
            { name: "Products", href: "/products" },
            ...(categoryName && initialCategorySlugs?.length
              ? [
                  {
                    name: categoryName,
                    href: `/category/${initialCategorySlugs[0]}`,
                  },
                ]
              : []),
          ]}
        />
      </div>
      <div className="container flex my-6 mx-auto gap-6 px-3 xl:px-0">
        {/* ── Sidebar Filter Panel ── */}
        <div className="hidden xl:flex flex-col justify-start max-w-[280px] w-full h-screen sticky top-48">
          <div
            className="flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-y-auto"
            style={{ height: "calc(100vh - 219px)" }}
          >
            {/* Sidebar Header */}
            <div className="flex items-center gap-2.5 px-5 py-2 border-b border-gray-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <Typography
                tag="h2"
                className="text-base font-bold text-gray-800"
              >
                Filters
              </Typography>
            </div>
            {filterPanel}
          </div>
        </div>

        {/* ── Main Content ── */}
        <div className="flex flex-col w-full gap-4">
          {/* ── Search + Sort Row ── */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="relative mx-auto md:mx-0 w-full md:max-w-sm xl:max-w-lg group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400 group-focus-within:text-primary transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search products..."
                className="transition-all w-full py-3 pl-11 pr-10 text-sm bg-white border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-gray-400"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
              />
              {searchQuery && (
                <button
                  className="absolute top-1/2 right-3 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  onClick={clearSearchQuery}
                >
                  <Image
                    src="/img/cross.svg"
                    alt="Clear"
                    height={16}
                    width={16}
                  />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div ref={sortRef} className="relative">
                <button
                  onClick={() => setIsSortOpen((prev) => !prev)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <span className="text-gray-400 font-normal">Sort by:</span>
                  <span className="text-gray-800">
                    {selectedSortOption.title}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 transition-transform ${isSortOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isSortOpen && (
                  <div className="absolute right-0 z-20 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 animate-in fade-in">
                    {sortOptionsList.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          handleSelectSortOption(option);
                          setIsSortOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                          selectedSortOption.id === option.id
                            ? "text-primary font-medium bg-primary/5"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {option.title}
                        {selectedSortOption.id === option.id && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-primary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="xl:hidden flex shrink-0">
                <button
                  onClick={() => setIsFilterModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Image
                    src="/img/filter.svg"
                    alt="filters"
                    height={18}
                    width={18}
                  />
                  Filters
                </button>
              </div>
            </div>
          </div>

          {/* ── Active Filters Toolbar ── */}
          <div className="flex flex-col md:flex-row justify-between md:items-center bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-3">
            <div className="flex gap-2 sm:gap-3 items-center flex-wrap">
              <Typography
                tag="p"
                className="text-sm text-gray-400 text-nowrap font-medium"
              >
                Active Filters:
              </Typography>
              <div className="flex items-center flex-wrap gap-2 md:max-w-md lg:max-w-2xl xl:max-w-xl 2xl:max-w-4xl">
                {selectedCategory.map((category) => (
                  <DismissibleChip
                    key={category}
                    label={getCategoryNameFromSlug(category)}
                    onRemove={() => handleRemoveFilter(category)}
                  />
                ))}
                {selectedPrice !== priceRange[0].title && (
                  <DismissibleChip
                    key="price"
                    label={selectedPrice}
                    onRemove={handleRemovePriceFilter}
                  />
                )}
                {selectedDiscount && (
                  <DismissibleChip
                    key="discount"
                    label={selectedDiscount}
                    onRemove={handleRemoveDiscountFilter}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 md:mt-0">
              <div className="flex items-center gap-1.5">
                <span className="text-lg font-bold text-primary">
                  {products?.pages[0]?.data?.total}
                </span>
                <Typography
                  tag="p"
                  className="text-sm font-medium text-gray-400"
                >
                  Results found
                </Typography>
              </div>
              {isFilterApplied && (
                <button
                  onClick={handleClearAllFilters}
                  className="transition-all text-sm font-medium text-gray-500 hover:text-red-500 flex items-center gap-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* ── Product Grid ── */}
          <div className="flex flex-col">
            <InfiniteLoader
              loadMore={fetchNextPage}
              hasMore={hasNextPage}
              loading={isFetchingNextPage}
            >
              {products?.pages?.[0]?.data?.data?.length === 0 ? (
                <div className="flex flex-col items-center justify-center my-36">
                  <Image
                    src="/img/no-products-found.webp"
                    alt="No products found"
                    width={260}
                    height={300}
                  />
                  <Typography
                    tag="h2"
                    className="font-semibold text-2xl text-gray-800 mt-6"
                  >
                    No Products Found
                  </Typography>
                  <Typography tag="p" className="text-gray-400 mt-2 text-sm">
                    Try adjusting your filters or search terms
                  </Typography>
                </div>
              ) : (
                <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {products?.pages?.map((p) =>
                    p.data.data.length
                      ? p.data.data.map((product) => (
                          <ProductCardUI
                            key={product.id}
                            product={product}
                            pageType="products"
                          />
                        ))
                      : null,
                  )}
                </div>
              )}
            </InfiniteLoader>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        withCloseButton
        size="medium"
      >
        {filterPanel}
      </Modal>
    </section>
  );
};

export default Products;
